import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import InfluencerSNSUpdateList from '../../components/admin/influencer/InfluencerSNSUpdateList';
import '../../styles/admin/AdminSummary.css';

const API_URL = process.env.REACT_APP_API_URL;

function InfluencerSNSUpdatePage() {
  const [selected, setSelected] = useState(null); // { ownerId, userId, userName, requestedAt }
  const [accounts, setAccounts] = useState([]);   // [{ id, platform, accountName, followerCount }]
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // draft: { snsId: number }
  const [drafts, setDrafts] = useState({});

  // 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAcc, setModalAcc] = useState(null);
  const [newCount, setNewCount] = useState('');

  useEffect(() => {
    const id = selected?.ownerId || selected?._id || selected?.userId;
    if (!id) { setAccounts([]); setDrafts({}); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/influencer/${id}/sns-refresh/detail`, { credentials: 'include' });
        const data = await res.json();
        if (res.ok && data?.ok) {
          setAccounts(data.accounts || []);
          setDrafts({}); // 선택 변경 시 draft 초기화
        } else {
          setAccounts([]); setDrafts({});
        }
      } catch { setAccounts([]); setDrafts({}); }
      setLoading(false);
    })();
  }, [selected]);

  // 화면 표시용(드래프트 반영)
  const mergedAccounts = useMemo(() => {
    return accounts.map(a => ({
      ...a,
      followerCount: drafts[a.id] !== undefined ? Number(drafts[a.id]) : a.followerCount
    }));
  }, [accounts, drafts]);

  const grouped = useMemo(() => {
    const g = { instagram: [], blog: [], youtube: [], tiktok: [] };
    for (const a of mergedAccounts) g[a.platform]?.push(a);
    return g;
  }, [mergedAccounts]);

  const openEdit = (acc) => {
    setModalAcc(acc);
    const current = drafts[acc.id] !== undefined ? String(drafts[acc.id]) : String(acc.followerCount || '');
    setNewCount(current);
    setModalOpen(true);
  };

  // 모달 저장: 서버 전송 없이 draft에만 반영(여러 번 가능)
  const saveDraft = () => {
    const n = Number(newCount);
    if (!Number.isFinite(n) || n < 0) return alert('유효한 숫자를 입력하세요.');
    setDrafts(prev => ({ ...prev, [modalAcc.id]: n }));
    setModalOpen(false);
    setModalAcc(null);
  };

  // 최종 커밋: draft들을 일괄 반영 후 완료 처리 → 좌측 목록에서 제거
  const completeAll = async () => {
    const id = selected?.ownerId;
    if (!id) return;
    if (!window.confirm('현재 사용자에 대해 드래프트를 일괄 반영하고, 갱신 요청을 완료 처리할까요?')) return;

    // updates 배열 생성
    const updates = Object.keys(drafts).map(k => ({ snsId: k, followerCount: Number(drafts[k]) }))
      .filter(u => Number.isFinite(u.followerCount) && u.followerCount >= 0);

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/${id}/sns-refresh/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ updates })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setSelected(null);
        setAccounts([]);
        setDrafts({});
        setRefreshToken(t => t + 1); // 좌측 목록 갱신(제거)
      } else {
        alert(data?.message || '완료 처리 실패');
      }
    } catch {
      alert('요청 실패');
    }
    setLoading(false);
  };

  const hasAny = (accounts || []).length > 0;

  return (
    <AdminLayout title="SNS 팔로워 수 갱신">
      <div className="admin-grid">
        <div className="admin-section" style={{ height: 'calc(100vh - 120px)' }}>
          <InfluencerSNSUpdateList
            onSelect={(u) => setSelected(u)}
            selectedId={selected?.ownerId}
            refreshToken={refreshToken}
          />
        </div>

        <div className="admin-section admin-section--wide" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <h3 className="admin-summary__title">SNS 계정 갱신</h3>
          {loading && <div className="admin-summary__loading">불러오는 중...</div>}
          {!loading && !selected && <div className="admin-summary__empty" style={{ marginTop: '20px' }}>좌측에서 항목을 선택하세요.</div>}
          {!loading && selected && (
            <>
              <div style={{ marginBottom: 12, fontWeight: 600, fontSize: '14px', color: 'var(--admin-text-main)' }}>
                대상: {selected.userId} ({selected.userName})
              </div>

              {['instagram','blog','youtube','tiktok'].map(p => (
                <div key={p} style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '14px', color: 'var(--admin-color-primary)' }}>
                    {p === 'instagram' ? '인스타그램' : p === 'blog' ? '블로그' : p === 'youtube' ? '유튜브' : '틱톡'}
                  </div>
                  {(grouped[p] || []).length === 0 ? (
                    <div style={{ color: 'var(--admin-text-sub)', fontSize: '14px' }}>요청된 계정 없음</div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {grouped[p].map(acc => (
                        <li key={acc.id} style={{ display: 'flex', alignItems:'center', justifyContent: 'space-between', gap:8, padding: '12px', marginBottom: '8px', background: 'var(--admin-bg-body)', borderRadius: '6px', border: '1px solid var(--admin-border-color)' }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-main)' }}>@{acc.accountName}</span>
                          <span style={{ color: 'var(--admin-text-sub)', fontSize: '14px' }}>{Number(acc.followerCount).toLocaleString()}</span>
                          <button className="admin-section__button admin-section__button--secondary" type="button" onClick={() => openEdit(acc)} style={{ padding: '6px 12px', fontSize: '13px' }}>갱신하기</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              <div className="admin-section__actions" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
                <button 
                  className="admin-section__button" 
                  type="button" 
                  onClick={completeAll} 
                  disabled={!hasAny}
                  style={{ backgroundColor: !hasAny ? '#b2dfdb' : 'var(--admin-color-success)', borderColor: !hasAny ? '#80cbc4' : 'var(--admin-color-success)' }}
                >
                  갱신 완료
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 갱신 입력 모달(드래프트 저장) */}
      {modalOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'var(--admin-bg-panel)', padding: 24, width:400, borderRadius: 'var(--admin-card-radius)', boxShadow: 'var(--admin-shadow-xl)' }}>
            <h3 style={{ marginTop:0, color: 'var(--admin-text-main)' }}>팔로워 수 갱신</h3>
            <div style={{ marginBottom:12, fontSize: '14px', color: 'var(--admin-text-body)' }}>@{modalAcc?.accountName}</div>
            <input
              type="number"
              value={newCount}
              onChange={(e)=>setNewCount(e.target.value)}
              style={{ width:'100%', height:40, padding: '8px 12px', border: '1px solid var(--admin-border-color)', borderRadius: 'var(--admin-input-radius)', fontSize: '14px' }}
            />
            <div className="admin-section__actions" style={{ justifyContent:'flex-end', marginTop:16 }}>
              <button className="admin-section__button admin-section__button--secondary" onClick={()=>{ setModalOpen(false); setModalAcc(null); }}>취소</button>
              <button className="admin-section__button" onClick={saveDraft} style={{ backgroundColor: 'var(--admin-color-success)', borderColor: 'var(--admin-color-success)' }}>저장(드래프트)</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default InfluencerSNSUpdatePage;